import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import RespectIcon, { RespectState } from '../feed/RespectIcon';
import { RespectHoverCard } from './RespectHoverCard';
import { ProfileCircleStack } from './ProfileCircleStack';
import { Responder, MutualRespectPair } from '../../types/respect';
import { useCompletedConnections } from '../../hooks/useCompletedConnections';

interface PostRespectAreaProps {
  postId: string;
  postAuthorId: string;
  className?: string;
}

/**
 * PostRespectArea Component
 *
 * Toont het infinity-symbool (respect state) + gestapelde avatars van wie heeft
 * gereageerd. Bij klik opent een warm verbindingspunt (RespectHoverCard).
 *
 * Respect ontstaat ALLEEN door reageren — er is geen klikknop.
 * Geen tellingen, geen ranking, geen scores.
 */
export const PostRespectArea: React.FC<PostRespectAreaProps> = ({
  postId,
  postAuthorId,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [responders, setResponders] = useState<Responder[]>([]);
  const [mutualPairs, setMutualPairs] = useState<MutualRespectPair[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Set of userIds the current user has completed the full ConnectieFlow with
  const superKonnectedIds = useCompletedConnections();

  const hasInteraction = responders.length > 0;
  const hasMutualRespect = mutualPairs.length > 0;

  // Onafhankelijke states per icoon
  const leftState: RespectState  = hasInteraction   ? 'RESPECT'        : 'INACTIEF';
  const rightState: RespectState = hasMutualRespect ? 'MUTUAL_RESPECT' : 'INACTIEF';

  // Unieke users uit mutual pairs voor de avatar stack
  const mutualUsers: Responder[] = [];
  const seen = new Set<string>();
  mutualPairs.forEach(pair => {
    if (!seen.has(pair.userA.id)) { seen.add(pair.userA.id); mutualUsers.push(pair.userA); }
    if (!seen.has(pair.userB.id)) { seen.add(pair.userB.id); mutualUsers.push(pair.userB); }
  });

  // Helper: haal user profile op voor een userId + voeg SK badge toe indien van toepassing
  const fetchProfile = async (userId: string): Promise<Responder> => {
    try {
      const snap = await getDoc(doc(db, 'userProfiles', userId));
      if (snap.exists()) {
        const d = snap.data();
        return {
          id: userId,
          displayName: d.displayName || 'Gebruiker',
          photoURL: d.photoURL || '',
          isSuperKonnected: superKonnectedIds.has(userId),
        };
      }
    } catch {}
    return { id: userId, displayName: 'Gebruiker', photoURL: '', isSuperKonnected: false };
  };

  // Realtime listener voor respectRelations
  useEffect(() => {
    const q = query(collection(db, 'respectRelations'), where('postId', '==', postId));
    const unsub = onSnapshot(q, async (snap) => {
      try {
        const list: Responder[] = await Promise.all(
          snap.docs.map(d => fetchProfile(d.data().responderId))
        );
        setResponders(list);
      } catch (error) {
        console.error('Error loading responders:', error);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Respect listener error:', error);
      setLoading(false);
    });
    return () => unsub();
  }, [postId]);

  // Realtime listener voor mutualRespectRelations
  useEffect(() => {
    const q = query(collection(db, 'mutualRespectRelations'), where('postId', '==', postId));
    const unsub = onSnapshot(q, async (snap) => {
      try {
        const pairs: MutualRespectPair[] = await Promise.all(
          snap.docs.map(async (d) => {
            const { userA, userB } = d.data();
            const [profileA, profileB] = await Promise.all([
              fetchProfile(userA),
              fetchProfile(userB),
            ]);
            return { userA: profileA, userB: profileB };
          })
        );
        setMutualPairs(pairs);
      } catch (error) {
        console.error('Error loading mutual pairs:', error);
      }
    }, (error) => {
      console.error('Mutual respect listener error:', error);
    });
    return () => unsub();
  }, [postId]);

  useEffect(() => {
    const handleClose = () => setIsOpen(false);
    window.addEventListener('closeRespectCard', handleClose);
    return () => window.removeEventListener('closeRespectCard', handleClose);
  }, []);

  if (loading) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Alleen openen als er iets te tonen is
    if (hasInteraction || hasMutualRespect) {
      setIsOpen(prev => !prev);
    }
  };

  return (
    <div
      className={`relative w-full ${className}`}
      style={{ borderTop: '1px solid #f0f0ee', paddingTop: 12 }}
    >
      <div
        className="respect-row flex items-center justify-between w-full"
        onClick={handleClick}
        style={{ cursor: hasInteraction || hasMutualRespect ? 'pointer' : 'default' }}
        role={hasInteraction || hasMutualRespect ? 'button' : undefined}
        aria-label={hasInteraction || hasMutualRespect ? 'Bekijk wie heeft gereageerd' : undefined}
      >
        {/* Linker zijde: Respect */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <RespectIcon state={leftState} size={64} />
          <div className="flex flex-col gap-1 min-w-0">
            <span
              style={{
                fontFamily: "'DM Sans',-apple-system,sans-serif",
                fontSize: 11,
                fontWeight: 300,
                letterSpacing: '0.04em',
                color: hasInteraction ? '#6B9E8A' : '#A8ADBA',
                transition: 'color 0.35s ease',
                whiteSpace: 'nowrap',
              }}
            >
              respect
            </span>
            {hasInteraction && (
              <ProfileCircleStack responders={responders} maxVisible={9} />
            )}
          </div>
        </div>

        {/* Scheidingslijn */}
        <div style={{ width: 1, height: 36, background: '#E4E6EC', flexShrink: 0, margin: '0 8px' }} aria-hidden="true" />

        {/* Rechter zijde: Mutual Respect */}
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <div className="flex flex-col gap-1 items-end min-w-0">
            <span
              style={{
                fontFamily: "'DM Sans',-apple-system,sans-serif",
                fontSize: 11,
                fontWeight: 300,
                letterSpacing: '0.04em',
                color: hasMutualRespect ? '#6B9E8A' : '#A8ADBA',
                transition: 'color 0.35s ease',
                whiteSpace: 'nowrap',
              }}
            >
              mutual respect
            </span>
            {hasMutualRespect && (
              <ProfileCircleStack responders={mutualUsers} maxVisible={9} />
            )}
          </div>
          <RespectIcon state={rightState} size={64} />
        </div>
      </div>

      {isOpen && (
        <RespectHoverCard
          responders={responders}
          mutualPairs={mutualPairs}
          isVisible={isOpen}
          postId={postId}
        />
      )}
    </div>
  );
};

export default PostRespectArea;
